/**
 * Restaurant checkout page.
 *
 * Reads the client-side restaurant cart (zustand + localStorage) and
 * submits it to POST /api/orders/restaurant. On success the cart is
 * cleared and the user is sent to the standard order confirmation
 * screen, so restaurant orders show up in /orders alongside grocery
 * orders.
 *
 * History: replaces the "Restaurant checkout coming soon!" toast that
 * was the dead-end on /restaurants/:id and on the cart page's
 * restaurant card.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { ArrowLeft, MapPin, Utensils } from 'lucide-react';
import { restaurantsAPI } from '../api/restaurants';
import { useRestaurantCart } from '../stores/restaurantCart';
import { useAuthStore } from '../stores/authStore';

export function RestaurantCheckoutPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const cart = useRestaurantCart();
  const clear = cart.clear;

  const [address, setAddress] = useState('');
  const [instructions, setInstructions] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const subtotal = cart.total();
  const deliveryFee = subtotal > 0 ? 30 : 0;
  const total = subtotal + deliveryFee;

  if (!user) {
    navigate('/login');
    return null;
  }

  if (!cart.restaurantId || cart.lines.length === 0) {
    return (
      <Container className="py-5">
        <Button variant="link" className="ps-0" onClick={() => navigate('/restaurants')}>
          <ArrowLeft size={18} className="me-1" /> Back to restaurants
        </Button>
        <Card className="text-center py-5 border-0 shadow-sm mt-3">
          <Card.Body>
            <Utensils size={48} className="text-muted mb-3" />
            <h5 className="text-muted mb-4">Your restaurant cart is empty</h5>
            <Button variant="warning" onClick={() => navigate('/restaurants')}>
              Browse restaurants
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const handleSubmit = async () => {
    if (address.trim().length < 3) {
      setError('Please enter a delivery address.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const order = await restaurantsAPI.createOrder({
        restaurant_id: cart.restaurantId!,
        lines: cart.lines.map((l) => ({
          menu_item_id: l.menu_item_id,
          quantity: l.quantity,
        })),
        delivery_address: address.trim(),
        special_instructions: instructions.trim() || null,
      });
      // Clear the local cart only AFTER the server confirms the order
      // so a network failure doesn't lose the user's work.
      clear();
      // Route name is /order-confirmed/:orderId — see App.tsx. Earlier code
      // pointed at /orders/:id/confirmation which 404s after a successful
      // POST and made the user think "place order" had failed.
      navigate(`/order-confirmed/${order.id}`, { replace: true });
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to place order. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container className="py-4">
      <Button variant="link" className="ps-0 text-warning" onClick={() => navigate('/cart')}>
        <ArrowLeft size={18} className="me-1" /> Back to cart
      </Button>
      <h2 className="fw-bold mb-4">
        <Utensils size={28} className="me-2" />
        Restaurant Checkout
      </h2>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <Row>
        <Col lg={8}>
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-warning-subtle">
              <strong>{cart.restaurantName}</strong>
            </Card.Header>
            <Card.Body>
              {cart.lines.map((l) => (
                <div key={l.menu_item_id} className="d-flex justify-content-between mb-2">
                  <span>{l.name} <small className="text-muted">× {l.quantity}</small></span>
                  <strong>₹{(l.price * l.quantity).toFixed(2)}</strong>
                </div>
              ))}
            </Card.Body>
          </Card>

          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-light">
              <MapPin size={16} className="me-2" />
              <strong>Delivery details</strong>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Delivery address *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Flat / House no, Street, Area, City"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Special instructions <small className="text-muted">(optional)</small></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="No onions, ring the bell twice, etc."
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                />
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="shadow-sm">
            <Card.Header className="bg-light"><strong>Bill summary</strong></Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between mb-2">
                <span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Delivery fee</span><span>₹{deliveryFee.toFixed(2)}</span>
              </div>
              <hr />
              <div className="d-flex justify-content-between fw-bold fs-5 mb-3">
                <span>Total</span>
                <span className="text-warning">₹{total.toFixed(2)}</span>
              </div>
              <div className="d-grid">
                <Button
                  variant="warning"
                  size="lg"
                  disabled={submitting}
                  onClick={handleSubmit}
                  className="fw-bold"
                >
                  {submitting ? <Spinner size="sm" animation="border" /> : `Place order · ₹${total.toFixed(0)}`}
                </Button>
              </div>
              <small className="text-muted d-block mt-2 text-center">
                Pay on delivery • You can cancel before the restaurant accepts.
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default RestaurantCheckoutPage;

